import { debounce } from '@theme/utilities';

/**
 * Frelan trade cart: the quantity-break slider on a cart line
 * (snippets/cart-line-pricing.liquid).
 *
 * The control is a native <input type="range"> (0–1000) over a drawn track. Each price tier
 * gets an equal share of the track; the last tier runs to `data-cap`. While the handle moves,
 * the quantity, price each and line total are previewed here, in the browser, with no
 * request. When it is let go (or after a pause on the keyboard) Shopify is asked ONCE,
 * through cart-items-component's setLineQuantity, and the re-rendered cart replaces the
 * preview — Liquid stays the source of truth for prices.
 *
 * A plain HTMLElement on purpose: Horizon treats a `*-component` tag as a ref boundary, and
 * this sits inside cart-items-component, which owns the line's refs.
 */

const MAX = 1000;
const COMMIT_DELAY = 450;

/**
 * Formats pence with the shop's money format ("£{{amount}}" etc.).
 * @param {number} cents
 * @param {string} format
 * @returns {string}
 */
function formatMoney(cents, format) {
  const value = cents / 100;

  /**
   * @param {number} number
   * @param {number} decimals
   * @param {string} thousands
   * @param {string} decimal
   */
  const group = (number, decimals, thousands, decimal) => {
    const [whole = '0', fraction] = number.toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
  };

  return (format || '{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return group(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(value, 0, '.', ',');
      case 'amount_with_space_separator':
        return group(value, 2, ' ', ',');
      default:
        return group(value, 2, ',', '.');
    }
  });
}

/**
 * @typedef {{ min: number, price: number }} Tier
 */

class CartQuantitySlider extends HTMLElement {
  static get observedAttributes() {
    return ['data-quantity', 'data-tiers', 'data-cap'];
  }

  /** @type {HTMLInputElement | null} */
  #input = null;

  /** @type {Tier[]} */
  #tiers = [];

  #cap = 1;

  #quantity = 1;

  /** @type {number | null} The previewed quantity not yet sent to Shopify. */
  #pending = null;

  #commit = debounce(() => this.#send(), COMMIT_DELAY);

  connectedCallback() {
    this.#input = this.querySelector('.cart-qty-slider__input');
    this.#read();

    this.#input?.addEventListener('input', this.#onInput);
    this.#input?.addEventListener('change', this.#onChange);
    this.#input?.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('input', this.#onInput);
    this.#input?.removeEventListener('change', this.#onChange);
    this.#input?.removeEventListener('keydown', this.#onKeydown);
    this.#commit.cancel();
  }

  /** The cart re-render (morph) updates the data attributes: take the server's values. */
  attributeChangedCallback() {
    this.#read();
  }

  #read() {
    try {
      this.#tiers = JSON.parse(this.dataset.tiers || '[]');
    } catch {
      this.#tiers = [];
    }
    this.#cap = Number(this.dataset.cap) || 1;
    this.#quantity = Number(this.dataset.quantity) || 1;
    this.#pending = null;
  }

  /**
   * @param {number} quantity
   * @returns {number} The tier index that quantity falls in.
   */
  #tierIndex(quantity) {
    let index = 0;
    this.#tiers.forEach((tier, i) => {
      if (quantity >= tier.min) index = i;
    });
    return index;
  }

  /**
   * @param {number} index
   * @returns {[number, number]} The tier's quantity range (its minimum, the next tier's minimum).
   */
  #range(index) {
    const low = this.#tiers[index]?.min ?? 1;
    const high = this.#tiers[index + 1]?.min ?? this.#cap;
    return [low, Math.max(high, low + 1)];
  }

  /**
   * Same mapping as the Liquid that places the handle on render.
   * @param {number} quantity
   * @returns {number} 0–1000
   */
  toPosition(quantity) {
    const count = this.#tiers.length;
    if (!count) return 0;
    const segment = MAX / count;
    const index = this.#tierIndex(quantity);
    const [low, high] = this.#range(index);
    const within = Math.min(1, Math.max(0, (quantity - low) / (high - low)));
    return Math.round(index * segment + within * segment);
  }

  /**
   * @param {number} position 0–1000
   * @returns {number} The quantity at that point on the track.
   */
  toQuantity(position) {
    const count = this.#tiers.length;
    if (!count) return this.#quantity;
    const segment = MAX / count;
    const index = Math.min(count - 1, Math.floor(position / segment));
    const [low, high] = this.#range(index);
    const within = (position - index * segment) / segment;
    return Math.max(this.#tiers[0]?.min ?? 1, Math.round(low + within * (high - low)));
  }

  #onInput = () => {
    if (!this.#input) return;
    this.#preview(this.toQuantity(Number(this.#input.value)));
  };

  #onChange = () => {
    this.#commit();
  };

  /**
   * Keys move by one unit (the track itself is 1000 steps, which would skip or stall units).
   * Page Up / Down jump a tier; Home / End go to the ends.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    const current = this.#pending ?? this.#quantity;
    const index = this.#tierIndex(current);
    let next;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = current + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = current - 1;
        break;
      case 'PageUp':
        next = this.#tiers[index + 1]?.min ?? this.#cap;
        break;
      case 'PageDown':
        next = current > (this.#tiers[index]?.min ?? 1) ? this.#tiers[index]?.min : this.#tiers[index - 1]?.min;
        break;
      case 'Home':
        next = this.#tiers[0]?.min;
        break;
      case 'End':
        next = this.#cap;
        break;
      default:
        return;
    }

    event.preventDefault();
    if (next === undefined) return;

    next = Math.max(this.#tiers[0]?.min ?? 1, next);
    if (this.#input) this.#input.value = String(this.toPosition(next));
    this.#preview(next);
    this.#commit();
  };

  /**
   * Shows a quantity everywhere on the line without asking Shopify.
   * @param {number} quantity
   */
  #preview(quantity) {
    this.#pending = quantity;

    const index = this.#tierIndex(quantity);
    const unit = this.#tiers[index]?.price ?? 0;
    const format = this.dataset.moneyFormat || '{{amount}}';
    const unitText = formatMoney(unit, format);
    const totalText = formatMoney(unit * quantity, format);
    const eachText = (this.dataset.eachTemplate || '[price]').replace('[price]', unitText);

    this.style.setProperty('--pos', `${this.toPosition(quantity) / 10}%`);
    this.querySelectorAll('[data-tier]').forEach((tier) => {
      tier.classList.toggle('is-active', Number(tier.getAttribute('data-tier')) === index);
    });
    this.#input?.setAttribute(
      'aria-valuetext',
      (this.dataset.valueTemplate || '[quantity]').replace('[quantity]', String(quantity)).replace('[price]', unitText)
    );

    // The line this slider belongs to: a trade drawer panel or a cart page row.
    const line = this.closest('[data-line-key], .cart-items__table-row');
    if (line) {
      const input = line.querySelector('.quantity-selector input');
      if (input instanceof HTMLInputElement) input.value = String(quantity);

      const total = line.querySelector('[data-line-total]');
      if (total) {
        total.textContent = totalText;
        total.setAttribute('value', totalText);
      }

      const each = line.querySelector('[data-line-each]');
      if (each) each.textContent = eachText;
    }

    // The trade drawer's list row for the same line.
    const key = this.dataset.key;
    const row = key ? this.closest('trade-cart')?.querySelector(`.trade-cart__row[data-line-key="${CSS.escape(key)}"]`) : null;
    if (row) {
      const qty = row.querySelector('[data-row-qty]');
      if (qty) qty.textContent = `× ${quantity}`;
      const rowEach = row.querySelector('[data-row-each]');
      if (rowEach) rowEach.textContent = unitText;
      const rowTotal = row.querySelector('[data-row-total]');
      if (rowTotal) rowTotal.textContent = totalText;
    }
  }

  #send() {
    const quantity = this.#pending;
    this.#pending = null;
    if (quantity === null || quantity === this.#quantity) return;

    const cartItems = /** @type {any} */ (this.closest('cart-items-component'));
    cartItems?.setLineQuantity?.({ line: Number(this.dataset.line), quantity });
  }
}

if (!customElements.get('cart-quantity-slider')) {
  customElements.define('cart-quantity-slider', CartQuantitySlider);
}
